import React, {useMemo, useState} from 'react';
import {Center, Flex, Box, Divider, Container, VStack, Button} from '@chakra-ui/react';
import {useNavigate} from 'react-router-dom';
import BackButton from '../../components/BackButton';
import {H3, TextA18} from '../../components/typography';
import MessageCard from '../../components/MessageCard';
import {useGetUserMessages, useMarkAllMessagesAsRead} from '../../hooks/useMessages';
import {UserMessage} from '../../types/message';
import BottomSheetSelect from '../../components/BottomSheetSelect';
import Icon from '../../components/Icon';
import {SpinnerLoader} from '../../components/Loader';

const Notifications = () => {
  const navigate = useNavigate();
  const {data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage} = useGetUserMessages(true);
  const messagesPages = data?.pages;
  const [menuOpen, setMenuOpen] = useState(false);
  const markAllAsRead = useMarkAllMessagesAsRead();

  const menuOptions = [
    {
      option: 'mark_all_read',
      text: 'Mark all as read',
      color: 'blue',
      renderIcon: (style) => <Icon iconName="fi-rr-check-double" {...style} />,
    },
  ];

  const handleMenuOption = async (option: string) => {
    if (option === 'mark_all_read') {
      await markAllAsRead.mutateAsync();
    }
    setMenuOpen(false);
  };

  const body = useMemo(
    () =>
      messagesPages ? (
        <>
          {messagesPages.map((pages, pageIndex) => (
            <React.Fragment key={`page-${pages.data.meta?.current_page || pageIndex}`}>
              {pages.data.data.map((message: UserMessage) => (
                <VStack key={message.uuid} spacing={0} align="stretch">
                  <MessageCard {...message} />
                  <Divider />
                </VStack>
              ))}
            </React.Fragment>
          ))}
        </>
      ) : (
        <TextA18>You have no messages yet</TextA18>
      ),
    [messagesPages]
  );

  return (
    <Container h="100vh" p={0} display="flex" flexDirection="column">
      {/* Fixed Header */}
      <Box bg="white" zIndex={10} position="sticky" top={0}>
        <Flex align="center" justify="center" py={4} px={4} position="relative">
          <BackButton
            onClick={() => navigate(-1)}
            sticky={false}
            position="absolute"
            left="16px"
            top="50%"
            transform="translateY(-50%)"
          />
          <H3>Notifications</H3>
          <Box
            position="absolute"
            right="16px"
            top="50%"
            transform="translateY(-50%)"
            cursor="pointer"
            onClick={() => setMenuOpen(true)}
          >
            <Icon
              iconName="fi-rr-menu-dots"
              style={{fontSize: '24px', width: '24px', height: '24px'}}
            />
          </Box>
        </Flex>
        <Divider />
      </Box>
      
      {/* Scrollable Content */}
      <Box flex="1" overflow="auto" position="relative">
        {isLoading ? (
          <SpinnerLoader height="100%" />
        ) : messagesPages && messagesPages[0].data.data.length === 0 ? (
          <Flex mt={4} height="100%" justifyContent="center" alignItems="center">
            <TextA18 fontWeight="700">You Don't Have Any Notifications Yet!</TextA18>
          </Flex>
        ) : (
          <Box>{body}</Box>
        )}
        {hasNextPage && (
          <Center mt={4} pb={4}>
            <Button 
              w="90%" 
              onClick={() => fetchNextPage()}
              isLoading={isFetchingNextPage}
              loadingText="Loading..."
            >
              Show More
            </Button>
          </Center>
        )}
        {markAllAsRead.isLoading && (
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bg="rgba(255, 255, 255, 0.8)"
            zIndex="999"
          >
            <SpinnerLoader height="100%" />
          </Box>
        )}
      </Box>
      <BottomSheetSelect
        options={menuOptions}
        openModal={menuOpen}
        onModalClose={() => setMenuOpen(false)}
        onClick={handleMenuOption}
      />
    </Container>
  );
};

export default Notifications;
